/**
 * Farbschema_Umschalter (ThemeModeSwitcher).
 *
 * Ein Icon-Button, der bei jedem Klick zyklisch zwischen den Farbschema-
 * Einstellungen `auto` → `hell` → `dunkel` → `auto` weiterschaltet. Das Icon
 * zeigt die aktuelle Einstellung an; der aktuelle Zustand wird zusätzlich
 * per `aria-label`/`title` zugänglich gemacht (Req 11.1: 44 px Tap-Ziel).
 *
 * Persistenz-Verhalten (analog zum Sprach_Umschalter): rein lokal über den
 * Farbschema-Cache (`localStorage`), kein Backend-Aufruf.
 */

import IconButton from "@mui/material/IconButton";
import BrightnessAutoRoundedIcon from "@mui/icons-material/BrightnessAutoRounded";
import LightModeRoundedIcon from "@mui/icons-material/LightModeRounded";
import DarkModeRoundedIcon from "@mui/icons-material/DarkModeRounded";
import { useThemeMode } from "../theme/AppThemeProvider";
import type { ThemeModePreference } from "../theme/themeModeStorage";
import { useI18n } from "../i18n/I18nProvider";

// ---------------------------------------------------------------------------
// Zustand + Zyklus
// ---------------------------------------------------------------------------

/** Zyklus-Reihenfolge beim Klicken. */
const MODE_CYCLE: ThemeModePreference[] = ["auto", "light", "dark"];

/** Icon je aktueller Einstellung. */
const MODE_ICONS: Record<
  ThemeModePreference,
  typeof BrightnessAutoRoundedIcon
> = {
  auto: BrightnessAutoRoundedIcon,
  light: LightModeRoundedIcon,
  dark: DarkModeRoundedIcon,
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ThemeModeSwitcher() {
  const { modePreference, setModePreference } = useThemeMode();
  const { t } = useI18n();

  const nextMode =
    MODE_CYCLE[(MODE_CYCLE.indexOf(modePreference) + 1) % MODE_CYCLE.length];
  const label = t(`themeModeSwitcher.${modePreference}`);
  const ModeIcon = MODE_ICONS[modePreference];

  return (
    <IconButton
      onClick={() => setModePreference(nextMode)}
      aria-label={label}
      title={label}
      sx={{ minWidth: 44, minHeight: 44 }}
    >
      <ModeIcon />
    </IconButton>
  );
}
